import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { API_BASE_URL } from '../../utils/apiConfig';
import { formatNumber } from '../../utils/formatNumber';

// Async thunk for fetching recent transactions
export const fetchRecentTransactions = createAsyncThunk(
  'transactions/fetchRecentTransactions',
  async ({ activeTab = 'Deposite', forceRefresh = false }, { rejectWithValue, getState }) => {
    // Check if we already have data for this tab and don't force refresh
    const state = getState();
    if (!forceRefresh && 
        state.transactions.activeTab === activeTab && 
        state.transactions.transactions.length > 0 &&
        !state.transactions.error) {
      // Return existing data without making API call
      return {
        transactions: state.transactions.transactions,
        activeTab: state.transactions.activeTab,
        rawData: state.transactions.rawData,
      };
    }

    try {
      // Build query params for latest 5 transactions
      const params = new URLSearchParams();
      const tabType =
        activeTab === 'Payouts'
          ? 'payout'
          : activeTab === 'Withdrawal'
            ? 'withdrawal'
            : 'deposit';
      params.append('type', tabType);
      params.append('limit', '5'); // Get only latest 5
      params.append('page', '1');
      params.append('sort', 'newest'); // Get newest first
      params.append('filter', 'range');

      const response = await fetch(
        `${API_BASE_URL}/dashboard/transactionList?${params.toString()}`,
        {
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        const dashboardData = data.data;

        // Ensure dashboardData.docs is an array
        let transactionsArray = [];
        if (Array.isArray(dashboardData.docs)) {
          transactionsArray = dashboardData.docs;
        }

        // Format transactions for display
        const formattedTransactions = transactionsArray.map((tx, index) => {
          const amount = Number(tx.amount) || 0;
          const actualBalance = Number(tx.actualBalance) || 0;

          // Determine display amount
          let displayAmount = amount;
          if (
            activeTab === 'Deposite' &&
            actualBalance !== undefined &&
            tx.status === 'confirmed'
          ) {
            displayAmount = actualBalance;
          }

          return {
            id: tx._id || `tx_${index}`,
            amount: `${formatNumber(displayAmount, 2)} USDT`,
            status:
              tx.status === 'confirmed' || tx.status === 'completed'
                ? 'Paid'
                : tx.status,
            destination: tx.walletAddress || tx.clientWallet || 'N/A',
            date: tx.createdAt
              ? new Date(tx.createdAt).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })
              : 'N/A',
            // Store raw data for reference
            rawData: tx,
          };
        });

        return {
          transactions: formattedTransactions,
          activeTab,
          rawData: dashboardData,
        };
      } else {
        return {
          transactions: [],
          activeTab,
          rawData: null,
        };
      }
    } catch (err) {
      console.error('Failed to fetch recent transactions:', err);
      return rejectWithValue(err.message || 'Failed to load recent transactions');
    }
  }
);

// Initial state
const initialState = {
  transactions: [],
  activeTab: 'Deposite',
  loading: false,
  error: null,
  rawData: null,
  lastFetched: null, // Track when data was last fetched
};

// Transactions slice
const transactionsSlice = createSlice({
  name: 'transactions',
  initialState,
  reducers: {
    clearTransactions: (state) => {
      // Reset all state to initial state
      state.transactions = [];
      state.activeTab = 'Deposite';
      state.loading = false;
      state.error = null;
      state.rawData = null;
      state.lastFetched = null;
    },
    setActiveTab: (state, action) => {
      state.activeTab = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch recent transactions
      .addCase(fetchRecentTransactions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRecentTransactions.fulfilled, (state, action) => {
        state.loading = false;
        state.transactions = action.payload.transactions;
        state.activeTab = action.payload.activeTab;
        state.rawData = action.payload.rawData;
        state.error = null;
        state.lastFetched = Date.now(); // Update timestamp
      })
      .addCase(fetchRecentTransactions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to load recent transactions';
        state.transactions = [];
      });
  },
});

// Export actions
export const { clearTransactions, setActiveTab } = transactionsSlice.actions;

// Export selectors
export const selectTransactions = (state) => state.transactions.transactions;
export const selectTransactionsLoading = (state) => state.transactions.loading;
export const selectTransactionsError = (state) => state.transactions.error;
export const selectActiveTab = (state) => state.transactions.activeTab;
export const selectTransactionsRawData = (state) => state.transactions.rawData;

// Export reducer
export default transactionsSlice.reducer;

